import type { Allowance, AttemptInput, AttemptResult, Comeback, Difficulty, Exam, PastExam, FirstKind, Health, ItemStatus, LearnerProfile, LearnEnd, LearnProfile, LearnStatus, LiveToken, Moment, MomentBlueprints, MomentItemStatus, MomentListPage, MomentSeed, MomentProgress, PlacementPublicTask, PlacementScored, PlacementSummary, ReviewGate, ReviewQueueEntry, RoundResponse, ServedPack, StoredFeedback, Usage, UsageReport, UsageTotals } from './types';
import { go } from './router';
import { forgetReturn, rememberReturn } from './return-to';
import { research, researchCreation } from './research-client';
import type { ResearchAttempt } from './research/delivery';
import type { Decision, HeardSegment } from './capture-rules.generated';
import { serverErrorText } from './i18n';
import type { ReminderSettings } from './reminder-state';
import type { BuildingSheet, DistrictSheet, NewcomersOffer, OwnTown } from './town/own-town';

/**
 * HTTP failure with the status attached, so callers can tell "server said no" (4xx) from transient errors.
 * `message` is already in the interface language (from the body's `code`); the server's own English
 * stays in `body.error`.
 */
export class HttpError extends Error {
  constructor(
    public status: number,
    message: string,
    /** The JSON error body, when there was one (refusals carry their reason and numbers). */
    public body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'HttpError';
  }

  /** The server's stable error code (kebab-case), when it sent one. */
  get code(): string | undefined {
    return typeof this.body?.code === 'string' ? this.body.code : undefined;
  }
}

/**
 * Can sending the same request again help? A lost connection, a 5xx, 408 or
 * 429 yes; any other 4xx is the server refusing it (the moment is gone, the
 * account is gone), and the same request will be refused again.
 */
export function isRetryable(e: unknown): boolean {
  return !(e instanceof HttpError) || e.status >= 500 || e.status === 408 || e.status === 429;
}

/** What the one capture button tells the server about a capture: route names and numbers, never words (ticket 44). */
export interface CaptureCounts {
  route: 'overheard' | 'asked' | 'retell';
  corrected: boolean;
  sure: number;
  from?: string;
  to?: string;
  /** The hearer failed or was slow, and the learner said who it was. */
  fallback?: boolean;
}

/** The hearer's reply (POST /api/moments/hear): the clip is held under `token` until Make, discard or ten minutes. */
export interface Heard {
  token: string;
  situation: string;
  segments: HeardSegment[];
  decision: Decision;
}

/** The account and target language a device draft was captured under (ticket 12). */
export interface DraftBinding {
  owner: string;
  target?: string;
}

/** An invitation the owner can still hand on or revoke (never its token). */
export interface OpenInvitation {
  id: string;
  createdAt: string;
  expiresAt: string;
}

export interface BetaUser {
  id: string;
  username: string;
  target: string;
  explain: string;
}

/** A circle as its member sees it (beta, v1 ticket 16): ids, names and flags, never content. */
export interface Circle {
  id: string;
  target: string;
  /** This learner keeps the circle (can make a new code). */
  keeper: boolean;
  /** A working code exists; the code itself is shown only when it is made. */
  hasCode: boolean;
  max: number;
  members: { id: string; name: string; keeper: boolean; you: boolean }[];
}

/** One friend as the learner sees them, by either path (beta): the flags say which, never content. */
export interface Friend { id: string; name: string; circle: boolean; direct: boolean }
/** A link or request the learner made that is still open: its id and expiry, never the secret; a request carries the text they typed. */
export interface FriendOffer { id: string; kind: 'link' | 'request'; name?: string; expiresAt: string }
/** A request waiting for the learner's answer; the name is the sender's username. */
export interface FriendRequest { id: string; name: string }
export interface FriendsList { username: string; max: number; consentSeen: boolean; friends: Friend[]; incoming: FriendRequest[]; offers: FriendOffer[] }
export type SocialActivityKind = 'friend_request' | 'incoming_army' | 'battle_report' | 'spoils';
export type SocialActivityState = 'available' | 'resolved' | 'withdrawn' | 'expired' | 'cancelled';
export interface SocialActivityItem {
  id: string; kind: SocialActivityKind; availableAt: string; indicatorShownAt: string | null; openedAt: string | null;
  resolvedAt: string | null; state: SocialActivityState;
  outcome: 'accepted' | 'declined' | 'defended' | 'auto_played' | 'report_read' | 'rebuilt' | 'withdrawn' | 'expired' | 'cancelled' | 'planted' | 'dropped' | null;
  name?: string; count?: number; side?: 'attacker' | 'defender'; band?: 'held' | 'draw' | 'breach' | 'sacked'; ready?: boolean;
  destination: string;
}
export interface SocialActivityList { items: SocialActivityItem[]; unread: number; actionable: number }
/** A fresh friend link, shown this once: the path (`/friend/<secret>`), the code to type, when it ends. */
export interface FriendLink { id: string; path: string; code: string; expiresAt: string }

const postJson = (url: string, body: unknown = {}) =>
  fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

export async function j<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let payload: Record<string, unknown> | undefined;
    try {
      const body = await res.json();
      if (body && typeof body === 'object' && !Array.isArray(body)) payload = body as Record<string, unknown>;
    } catch { /* ignore */ }
    // Beta mode: a 401 anywhere means the session is gone — take the learner
    // to sign-in (unless they are already signing in or redeeming an invite).
    // Device drafts listen too: they stay on the device, hidden, until the
    // same account signs in again (a refused password is not a lost session).
    if (res.status === 401) toSignIn();
    if (res.status === 401 && !/\/api\/auth\/(signin|claim|recover)$/.test(pathOf(res))) {
      // Save the destination while the session layer's cached account id is still available to bind it.
      window.dispatchEvent(new CustomEvent('li-session-lost'));
    }
    throw new HttpError(res.status, serverErrorText(res.status, payload), payload);
  }
  return res.json() as Promise<T>;
}

/** The session is gone: go to sign-in, unless already signing in or redeeming an invite. */
function toSignIn() {
  const p = location.pathname;
  if (p.startsWith('/signin') || p.startsWith('/join') || p.startsWith('/recover') || p.startsWith('/town-check') || p.startsWith('/prototype')) return;
  // Sign-in sends the learner back to this screen (in this tab; unsafe paths are not kept).
  rememberReturn(location.pathname + location.search + location.hash);
  // Past any leave guard: with the session gone, nothing on screen can be saved.
  go('/signin', true, true);
}

function pathOf(res: Response): string {
  try {
    return new URL(res.url, location.href).pathname;
  } catch {
    return '';
  }
}

/**
 * The device's own IANA timezone, sent when an account is created and on every
 * sign-in. The server validates it and measures the daily moment allowance in
 * it; the day key itself is always computed server-side from the server clock.
 */
function browserTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export const api = {
  health: () => fetch('/api/health').then((r) => j<Health>(r)),
  /** Beta: new moments left today. `limit: null` in personal mode (no allowance). */
  allowance: () => fetch('/api/allowance').then((r) => j<Allowance>(r)),
  /**
   * Paginated moment list with inline practice state — one request per page,
   * no per-moment progress fetches. Handles the legacy plain-array shape too.
   */
  listMoments: (opts: { limit?: number; offset?: number; q?: string; state?: 'all' | 'new' | 'practiced' } = {}) => {
    const p = new URLSearchParams();
    p.set('limit', String(opts.limit ?? 20));
    p.set('offset', String(opts.offset ?? 0));
    if (opts.q?.trim()) p.set('q', opts.q.trim());
    if (opts.state && opts.state !== 'all') p.set('state', opts.state);
    return fetch(`/api/moments?${p}`).then(async (r) => {
      const data = await j<MomentListPage | import('./types').MomentSummary[]>(r);
      if (Array.isArray(data)) return { moments: data, total: data.length, limit: data.length, offset: 0 };
      return data;
    });
  },
  getMoment: (id: string) => fetch(`/api/moments/${id}`).then((r) => j<Moment>(r)),
  deleteMoment: (id: string) => fetch(`/api/moments/${id}`, { method: 'DELETE' }).then((r) => j<{ ok: true }>(r)),
  /** Delete only the original recording; the lesson and progress stay. Returns the moment without it. */
  deleteAudio: (id: string) => fetch(`/api/moments/${id}/audio`, { method: 'DELETE' }).then((r) => j<Moment>(r)),
  createMoment: (audio: Blob | null, opts: {
    note?: string; durationSec?: number; clientId?: string; draft?: DraftBinding; capturedAt?: number;
    /** The one capture button (ticket 44): make it from the clip the server holds (`audio` null), its note's spans, and the counts. */
    token?: string; spans?: { start: number; end: number }[]; capture?: CaptureCounts;
    /** A voice message shared to Eaves: the server reads its type and length from the file. */
    shared?: boolean;
    researchAttempt?: ResearchAttempt;
  }) => {
    const fd = new FormData();
    if (audio) {
      const ext = audio.type.includes('mp4') ? 'm4a' : audio.type.includes('ogg') ? 'ogg' : 'webm';
      fd.append('audio', audio, `moment.${ext}`);
    }
    if (opts.token) fd.append('token', opts.token);
    if (opts.spans?.length) fd.append('spans', JSON.stringify(opts.spans));
    if (opts.capture) fd.append('capture', JSON.stringify(opts.capture));
    if (opts.shared) fd.append('shared', '1');
    if (opts.note) fd.append('note', opts.note);
    if (opts.durationSec) fd.append('durationSec', String(opts.durationSec));
    // Idempotency key: a retry after a lost response resumes the same moment
    // instead of creating a duplicate (see the device drafts).
    if (opts.clientId) fd.append('clientId', opts.clientId);
    // Which account and language this capture belongs to: the server refuses
    // it (and spends nothing) if the session no longer matches.
    if (opts.draft?.owner) fd.append('draftOwner', opts.draft.owner);
    if (opts.draft?.target) fd.append('draftTarget', opts.draft.target);
    // Beta keeps the recording 30 days from capture, not from upload — sent as
    // an elapsed age (seconds since capture), not an absolute time. Both ends
    // of that subtraction are readings of this device's own clock, so a phone
    // whose clock is wrong (even by weeks, in either direction) still reports
    // roughly the true elapsed time; only the device's absolute date would
    // have been wrong, and we never send that.
    if (opts.capturedAt !== undefined) fd.append('captureAgeSec', String(Math.max(0, (Date.now() - opts.capturedAt) / 1000)));
    const attempt = opts.researchAttempt ?? research.currentAttempt();
    return researchCreation(
      () => fetch('/api/moments', { method: 'POST', headers: research.headers(attempt), body: fd }).then((r) => j<Moment>(r)),
      attempt,
    );
  },
  /** `answer`: the newcomer moment whose question this note answers (v1 ticket 42). */
  createTextMoment: (note: string, clientId?: string, draft?: DraftBinding, answer?: string, extra?: { retell?: boolean; capture?: CaptureCounts; researchAttempt?: ResearchAttempt }) => {
    const attempt = extra?.researchAttempt ?? research.currentAttempt();
    return researchCreation(() => fetch('/api/moments/text', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...research.headers(attempt) },
      body: JSON.stringify({
        note, clientId, draftOwner: draft?.owner || undefined, draftTarget: draft?.target || undefined,
        ...(answer ? { answer: { momentId: answer } } : {}),
        ...(extra?.retell ? { retell: true } : {}),
        ...(extra?.capture ? { capture: extra.capture } : {}),
      }),
    }).then((r) => j<Moment>(r)), attempt);
  },
  /** Ticket 44: the clip is heard (paid, outside the two a day) and held for ten minutes under its token. */
  hear: (audio: Blob, durationSec: number, signal?: AbortSignal, attempt: ResearchAttempt | null = research.currentAttempt()) => {
    const fd = new FormData();
    const ext = audio.type.includes('mp4') ? 'm4a' : audio.type.includes('ogg') ? 'ogg' : 'webm';
    fd.append('audio', audio, `listen.${ext}`);
    fd.append('durationSec', String(Math.round(durationSec * 10) / 10));
    return fetch('/api/moments/hear', { method: 'POST', headers: research.headers(attempt), body: fd, signal }).then((r) => j<Heard>(r));
  },
  /** Starts the overheard analysis from the held clip while the learner reads the card. */
  hearStart: (token: string, body: { note?: string; spans?: { start: number; end: number }[] }) =>
    fetch(`/api/moments/hear/${encodeURIComponent(token)}/start`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).then((r) => j<{ started: boolean }>(r)),
  /** Drops the held clip and any analysis started from it; idempotent, and never worth an error on screen. */
  hearDiscard: (token: string) =>
    fetch(`/api/moments/hear/${encodeURIComponent(token)}/discard`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}', keepalive: true }).then(() => undefined, () => undefined),
  /** A spoken "what I wanted to say" note written down (paid, never a new moment); the clip is sent once, never kept. */
  transcribeNote: (audio: Blob) => {
    const fd = new FormData();
    const ext = audio.type.includes('mp4') ? 'm4a' : audio.type.includes('ogg') ? 'ogg' : 'webm';
    fd.append('audio', audio, `note.${ext}`);
    return fetch('/api/moments/text/transcribe', { method: 'POST', body: fd }).then((r) => j<{ text: string }>(r));
  },
  momentItems: (id: string) => fetch(`/api/moments/${id}/items`).then((r) => j<{ items: MomentItemStatus[]; focus: string[]; seeds?: MomentSeed[] }>(r)),
  /** The moment's lines as blueprints, the learner's words marked (v1 ticket 08). */
  momentBlueprints: (id: string) => fetch(`/api/moments/${id}/blueprints`).then((r) => j<MomentBlueprints>(r)),
  setItemStatus: (id: string, key: string, status: ItemStatus, options: { operationId?: string; expectedVersion?: number } = {}) => {
    // Removal retries must reuse the caller's operation ID. Generating one
    // here would turn a lost-response retry into a second, conflicting write.
    if (status === 'removed' && !options.operationId) throw new Error('removal requires a stable operation ID');
    const operationId = options.operationId;
    return fetch(`/api/moments/${id}/items/status`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, status, ...(operationId ? { operationId } : {}), ...(options.expectedVersion !== undefined ? { expectedVersion: options.expectedVersion } : {}) }),
    }).then((r) => j<{
      item: { key: string; status: ItemStatus } | null;
      previousStatus: ItemStatus | null;
      statusVersion: number | null;
      replayed?: boolean;
    }>(r));
  },
  /** Keep or un-keep one of a moment's seeds (v1 ticket 09); idempotent, so a retry is safe. */
  keepSeed: (id: string, key: string, keep: boolean) =>
    fetch(`/api/moments/${id}/seeds/${keep ? 'keep' : 'unkeep'}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key }) }).then((r) => j<{ seed: MomentSeed }>(r)),
  /**
   * A line of the moment (by index) or its recording was heard (v1 ticket 29): the glue words in it count
   * toward knowing them, once a line a day on the server. Fire and forget.
   */
  heard: (id: string, what: { line: number } | { recording: true }) =>
    fetch(`/api/moments/${id}/heard`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(what) }).then((r) => j<{ counted: number }>(r)),
  saveMission: (id: string, text: string) =>
    fetch(`/api/moments/${id}/mission`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }) }).then((r) => j<{ mission: string }>(r)),
  saveMissionReflection: (id: string, rfl: { tried?: boolean; understood?: boolean; stuck?: boolean; note?: string }) =>
    fetch(`/api/moments/${id}/mission/reflection`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rfl) }).then((r) => j<{ reflection: Moment['missionReflection'] }>(r)),
  /**
   * Beta support sharing: open this one moment (including its recording) to
   * the owner until `endSupportShare` — nothing else in the account.
   */
  shareForSupport: (id: string) =>
    fetch(`/api/moments/${id}/share`, { method: 'POST' }).then((r) => j<{ share: { momentId: string; sharedAt: string } }>(r)),
  /** Beta support sharing: end it; the owner's access stops with this request. */
  endSupportShare: (id: string) =>
    fetch(`/api/moments/${id}/share`, { method: 'DELETE' }).then((r) => j<{ ok: true }>(r)),
  liveToken: (id: string) => fetch(`/api/moments/${id}/live-token`, { method: 'POST' }).then((r) => j<LiveToken>(r)),
  audioUrl: (id: string) => `/api/moments/${id}/audio`,
  ttsUrl: (id: string, text: string) => `/api/moments/${id}/tts?text=${encodeURIComponent(text)}`,
  /** A typed moment's imagined scene read by two voices (made on first ask, then cached on the server). */
  sceneAudioUrl: (id: string) => `/api/moments/${id}/scene-audio`,
  reportUsage: (id: string, u: UsageReport) =>
    fetch(`/api/moments/${id}/usage`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(u) }).then((r) => j<Usage>(r)),
  usage: () => fetch('/api/usage').then((r) => j<UsageTotals>(r)),
  /** The lesson as served today; `fewer` when waiting reviews cut it (v1 ticket 30). */
  pack: (id: string) => fetch(`/api/moments/${id}/pack`).then((r) => j<ServedPack>(r)),
  /**
   * A moment's first lesson in the town (v1 ticket 41): the day's cut, fixed for the day, never the reopened
   * lesson's first looks, with the cards of it answered today (`answeredToday`), so a resumed lesson never grows.
   */
  firstPack: (id: string) => fetch(`/api/moments/${id}/pack?first=1`).then((r) => j<ServedPack>(r)),
  /** Buy a fresh pack for this moment (a paid operation; 402 when the AI allowance is out). */
  refreshPack: (id: string) =>
    fetch(`/api/moments/${id}/pack/refresh`, { method: 'POST' }).then((r) => j<ServedPack>(r)),
  /** `attemptId` (a client id) makes a retry of the same answer safe: the server counts it once. */
  recordAttempt: (id: string, a: AttemptInput & { attemptId?: string }) =>
    fetch(`/api/moments/${id}/attempts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(a) }).then((r) => j<AttemptResult>(r)),
  /** Browser interaction/acknowledgement is recorded separately from the authoritative answer save. */
  exerciseEvent: (momentId: string, exposureId: string, eventId: string, kind: 'hint' | 'retry' | 'reveal' | 'study' | 'browser_ack') =>
    fetch(`/api/moments/${momentId}/exercises/${encodeURIComponent(exposureId)}/events`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ eventId, kind }),
    }).then((r) => j<{ recorded: boolean }>(r)),
  /** Read the current learner's bounded exercise → served artifact → saved-answer chain. */
  exerciseEvidence: (exposureId: string, limit = 20) =>
    fetch(`/api/learn/exercises/${encodeURIComponent(exposureId)}?limit=${limit}`).then((r) => j<Record<string, unknown>>(r)),
  progress: (id: string) => fetch(`/api/moments/${id}/progress`).then((r) => j<MomentProgress>(r)),
  scorePronunciation: (id: string, audio: Blob, expected: string) => {
    const fd = new FormData();
    fd.append('audio', audio, 'snippet.webm');
    fd.append('expected', expected);
    return fetch(`/api/moments/${id}/pronounce`, { method: 'POST', body: fd }).then((r) => j<{ pass: boolean; tip: string }>(r));
  },
  reviewDue: (limit = 10) => fetch(`/api/review/due?limit=${limit}`).then((r) => j<{ queue: ReviewQueueEntry[]; dueCount: number; gate?: ReviewGate }>(r)),
  /** The daily round (v1 ticket 15): one queue a day in slots; a round left mid-way resumes where it stopped. */
  round: (reminder?: string) => fetch(reminder ? `/api/round?reminder=${encodeURIComponent(reminder)}` : '/api/round').then((r) => j<RoundResponse>(r)),
  learnStatus: () => fetch('/api/learn/status').then((r) => j<LearnStatus>(r)),
  /**
   * The end of a round, a tending round or a lesson in test mode (v1 ticket 37): the honest promise and the
   * firsts no end has shown yet. This call marks nothing; ack the firsts with `ackFirstsSeen` once they are
   * actually on screen. The device's time zone names the promise's day in personal mode; beta uses the account's.
   */
  learnEnd: (round = false) =>
    fetch('/api/learn/end', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ timezone: browserTimezone(), ...(round ? { round: true } : {}) }) }).then((r) => j<LearnEnd>(r)),
  /**
   * Marks firsts shown (v1 ticket 37), once the client has actually rendered them: the other half of
   * `learnEnd` above. Best effort — if this never lands, the same firsts simply come again at the next end.
   */
  ackFirstsSeen: (kinds: FirstKind[]) =>
    fetch('/api/learn/firsts/seen', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kinds }) }).then((r) => j<{ ok: true }>(r)),
  /**
   * Marks an old recording's comeback shown (v1 ticket 31), once it was on screen: the same count on the same
   * moment never comes back. Best effort, like `ackFirstsSeen`.
   */
  ackComebackSeen: (c: Comeback) =>
    fetch('/api/learn/comeback/seen', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ momentId: c.momentId, count: c.lines, new: c.new }) }).then((r) => j<{ ok: true }>(r)),
  /** Reminders (v1 ticket 38): the learner's settings and this learner's devices; the VAPID public key rides along. */
  reminders: () => fetch('/api/reminders').then((r) => j<ReminderSettings>(r)),
  setReminders: (patch: { time?: string; card?: boolean; raidAlerts?: boolean }) =>
    fetch('/api/reminders/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) }).then((r) => j<ReminderSettings>(r)),
  addReminderDevice: (body: { channel: 'push'; subscription: PushSubscriptionJSON; tz?: string } | { channel: 'sync'; tz?: string; fallback?: boolean }) =>
    fetch('/api/reminders/devices', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tz: browserTimezone(), ...body }) }).then((r) => j<{ id: string }>(r)),
  removeReminderDevice: (id: string) =>
    fetch(`/api/reminders/devices/${encodeURIComponent(id)}`, { method: 'DELETE' }).then((r) => (r.ok ? undefined : j<never>(r))),
  /** The first-round offer was used (the learner went to Reminders) or dismissed. */
  reminderOffer: (used: boolean) =>
    fetch('/api/reminders/offer', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ used }) }).then((r) => j<ReminderSettings>(r)),
  resumeReminders: () =>
    fetch('/api/reminders/resume', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }).then((r) => j<ReminderSettings>(r)),
  /** The learner's own town (v1 ticket 12): districts, buildings by material, lamps, ivy, roads. */
  town: () => fetch('/api/town').then((r) => j<OwnTown>(r)),
  /**
   * A moment has landed (v1 ticket 41): its district rose from the fog on screen, so it never waits in the town's
   * `landing` again. Send it only once the district is on screen; a lost reply lands it again another time. Idempotent.
   */
  ackLanded: (id: string) => fetch(`/api/moments/${id}/landed`, { method: 'POST' }).then((r) => j<{ ok: true }>(r)),
  /**
   * Names the town and lays its first stone (v1 ticket 40); `null` ("Name it later") lays the stone and keeps any
   * name. A name the server refuses is an HttpError with code `town-name-invalid`; check it first with
   * `normaliseTownName` (town-name.generated.ts), the server's own rule.
   */
  townName: (name: string | null) =>
    fetch('/api/town/name', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) })
      .then((r) => j<{ name?: string; foundedAt: string; stone: true }>(r)),
  /** A part of the first night was seen: at the map or Skip (`opening`), at "Not now" (`naming`). Idempotent. */
  /** Newcomers (v1 ticket 42): Someone else swaps the offer (null when none is left today). */
  newcomersSkip: (id: string) =>
    fetch('/api/newcomers/skip', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) })
      .then((r) => j<{ newcomers: NewcomersOffer | null }>(r)),
  /** Not now: no offer until the learner's next day, on any device. */
  newcomersNotNow: () =>
    fetch('/api/newcomers/not-now', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }).then((r) => j<{ ok: true }>(r)),
  /** Meet them: the scene written and saved as a moment (201, or 200 for a retry); it lands like any moment. */
  meetNewcomers: (id: string) =>
    fetch('/api/newcomers', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) }).then((r) => j<Moment>(r)),
  townSeen: (part: 'opening' | 'naming') =>
    fetch('/api/town/seen', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ part }) }).then((r) => j<{ ok: true }>(r)),
  /** Tending the town (v1 ticket 27). A word's key is lesson text, so it travels in the query, never the path. */
  townDistrict: (id: string) => fetch(`/api/town/districts/${id}`).then((r) => j<DistrictSheet>(r)),
  townRun: (id: string) => fetch(`/api/town/districts/${id}/run`).then((r) => j<{ queue: ReviewQueueEntry[] }>(r)),
  townBuilding: (key: string) => fetch(`/api/town/building?key=${encodeURIComponent(key)}`).then((r) => j<BuildingSheet>(r)),
  townBuildingCard: (key: string) =>
    fetch(`/api/town/building/card?key=${encodeURIComponent(key)}`).then((r) => j<{ queue: ReviewQueueEntry[] }>(r)),
  townTending: () => fetch('/api/town/tending').then((r) => j<{ due: string[]; dark: string[] }>(r)),
  townRoadCard: (a: string, b: string, key: string) =>
    fetch(`/api/town/roads/${a}/${b}/card?key=${encodeURIComponent(key)}`).then((r) => j<{ queue: ReviewQueueEntry[] }>(r)),
  createExam: () => fetch('/api/exams', { method: 'POST' }).then((r) => j<Exam>(r)),
  getExam: (id: string) => fetch(`/api/exams/${id}`).then((r) => j<Exam>(r)),
  /** Finished exams with their stored scores, newest first (v1 ticket 10). */
  pastExams: () => fetch('/api/exams').then((r) => j<{ exams: PastExam[] }>(r)),
  learnProfile: () => fetch('/api/learn/profile').then((r) => j<LearnProfile>(r)),
  getLearner: () => fetch('/api/learn/learner').then((r) => j<{ learner: LearnerProfile | null }>(r)),
  saveLearner: (patch: { goals?: string; prior?: string; canDo?: Record<string, boolean> }) =>
    fetch('/api/learn/learner', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) }).then((r) => j<{ learner: LearnerProfile }>(r)),
  placement: (answers: { id: string; value?: unknown }[], finish = false) =>
    fetch('/api/learn/placement', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ answers, finish }) }).then((r) => j<
      | { done: false; scored: PlacementScored[]; next: PlacementPublicTask; progress: { answered: number; total: number } }
      | { done: true; scored: PlacementScored[]; summary: PlacementSummary; learner: LearnerProfile }
    >(r)),
  saveFeedback: (scope: string, fb: { useful?: number; difficulty?: Difficulty; note?: string }) =>
    fetch('/api/feedback', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ scope, ...fb }) }).then((r) => j<{ feedback: StoredFeedback }>(r)),
  ttsGeneralUrl: (text: string) => `/api/tts?text=${encodeURIComponent(text)}`,
  /** Check whether a single-use beta invitation is still valid; claiming it and signing in happen separately. */
  invitation: (token: string) => fetch('/api/auth/invitation', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }) }).then(r => j<{ valid: true }>(r)),
  /** Claim an invitation for a pair (its id; `target` too, for a server from before pair ids). */
  claim: (token: string, username: string, password: string, choice: { pair?: string; target: string }) =>
    fetch('/api/auth/claim', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, username, password, pair: choice.pair, target: choice.target, timezone: browserTimezone() }) }).then((r) => j<{ user: BetaUser }>(r)),
  /** Beta accounts: username/password sign-in (any device). */
  signin: (username: string, password: string) =>
    fetch('/api/auth/signin', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, password, timezone: browserTimezone() }) }).then((r) => j<{ user: BetaUser }>(r)),
  /**
   * Beta accounts: redeem an owner-issued recovery link by setting a new
   * password. Succeeds signed in on this device; every earlier session of the
   * account is dropped and the learner's history is left as it was.
   */
  recover: (token: string, password: string) =>
    fetch('/api/auth/recover', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, password }) }).then((r) => j<{ user: BetaUser }>(r)),
  /** Beta accounts: log out on this device (the session stops working everywhere). */
  signout: () => {
    forgetReturn(); // a kept destination does not outlive a deliberate sign-out
    return fetch('/api/auth/signout', { method: 'POST' }).then((r) => j<{ ok: true }>(r));
  },
  /** Beta: may the signed-in learner invite testers (OWNER_USER_IDS)? */
  owner: () => fetch('/api/owner').then((r) => j<{ owner: boolean }>(r)),
  /** Beta owner: a single-use invitation's path (`/join/<token>`), shown this once (the server keeps only its hash). */
  mintInvitation: () =>
    fetch('/api/owner/invitations', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }).then((r) => j<{ id: string; path: string; expiresAt: string }>(r)),
  /** Beta owner: invitations not yet used, revoked or expired; ids and times only. */
  openInvitations: () => fetch('/api/owner/invitations').then((r) => j<{ invitations: OpenInvitation[] }>(r)),
  /** Beta owner: an open invitation stops working. */
  revokeInvitation: (id: string) =>
    fetch(`/api/owner/invitations/${encodeURIComponent(id)}/revoke`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }).then((r) => j<{ ok: true }>(r)),
  /** Beta circles: the learner's own circle, or null. */
  circle: () => fetch('/api/circle').then((r) => j<{ circle: Circle | null }>(r)),
  /** Beta circles: start one after the consent screen. The code comes back this once. */
  createCircle: () =>
    fetch('/api/circle', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ consent: true }) }).then((r) => j<{ circle: Circle; code: string }>(r)),
  /** Beta circles: keeper makes a new code; the old one stops working. */
  newCircleCode: () => fetch('/api/circle/code', { method: 'POST' }).then((r) => j<{ circle: Circle; code: string }>(r)),
  /** Beta circles: join by code after the consent screen (the code goes in the body, never the URL). */
  joinCircle: (code: string) =>
    fetch('/api/circle/join', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code, consent: true }) }).then((r) => j<{ circle: Circle }>(r)),
  /** Beta circles: leave at once. */
  leaveCircle: () => fetch('/api/circle/leave', { method: 'POST' }).then((r) => j<{ ok: true; left: boolean }>(r)),
  /** Beta direct friends: who the learner is friends with (either way) and their open links. */
  friends: () => fetch('/api/friends').then((r) => j<FriendsList>(r)),
  /** Beta social activity: the learner's own requests, armies, reports and spoils. */
  activity: () => fetch('/api/activity', { cache: 'no-store' }).then((r) => j<SocialActivityList>(r)),
  activityIndicator: (ids: string[]) => postJson('/api/activity/indicator', { ids }).then((r) => j<{ shown: number }>(r)),
  activityOpened: (id: string) => postJson(`/api/activity/${encodeURIComponent(id)}/opened`).then((r) => j<{ ok: true; openedAt: string }>(r)),
  activityRequestOpened: (id: string) => postJson(`/api/activity/friend-requests/${encodeURIComponent(id)}/opened`).then((r) => j<{ ok: true }>(r)),
  /** Beta direct friends: "Don't show this again" on the sender's consent screen (stored on the account, as a version of the list). */
  setFriendConsentSeen: (seen: boolean) =>
    fetch('/api/friends/consent-seen', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ seen }) }).then((r) => j<{ ok: true; consentSeen: boolean }>(r)),
  /** Beta direct friends: make a one-time link after the consent list. The secret comes back this once. */
  makeFriendLink: () => postJson('/api/friends/links', { consent: true }).then((r) => j<FriendLink>(r)),
  /** Beta direct friends: what a typed code or a link's secret opens (the secret goes in the body, never the URL). Changes nothing. */
  lookupFriendCode: (secret: string) =>
    postJson('/api/friends/codes/lookup', { code: secret }).then((r) => j<{ kind: 'friend'; name: string; already?: boolean } | { kind: 'circle' }>(r)),
  /** Beta direct friends: agree to be friends by this link. */
  acceptFriendLink: (secret: string) =>
    postJson('/api/friends/links/accept', { token: secret, consent: true }).then((r) => j<{ ok: true; friend: { id: string; name: string }; already?: boolean }>(r)),
  /** Beta direct friends: ask a username to be friends. Always `ok`; only a crossed request names the new friend. */
  sendFriendRequest: (username: string) =>
    postJson('/api/friends/requests', { username, consent: true }).then((r) => j<{ ok: true; friend?: { id: string; name: string } }>(r)),
  /** Beta direct friends: agree to a request after the consent screen naming its sender. */
  acceptFriendRequest: (id: string) =>
    postJson(`/api/friends/requests/${encodeURIComponent(id)}/accept`, { consent: true }).then((r) => j<{ ok: true; friend: { id: string; name: string }; already?: boolean }>(r)),
  /** Beta direct friends: decline a request; the sender is never told. */
  declineFriendRequest: (id: string) => postJson(`/api/friends/requests/${encodeURIComponent(id)}/decline`).then((r) => j<{ ok: true }>(r)),
  /** Beta direct friends: end a direct friendship at once. A 404 means there is none (already gone). */
  endFriendship: (id: string) => postJson(`/api/friends/${encodeURIComponent(id)}/end`).then((r) => j<{ ok: true }>(r)),
  /** Beta direct friends: an open link stops working. A 404 means it is gone already. */
  withdrawFriendOffer: (id: string) =>
    postJson(`/api/friends/offers/${encodeURIComponent(id)}/withdraw`).then((r) => j<{ ok: true }>(r)),
  /**
   * Beta accounts: who is signed in. Nobody is `{ user: null }` with a 200 (not a failed
   * request); it is handled like a 401 from any other route: the session is
   * over on this device and the app goes to sign-in.
   */
  me: () =>
    fetch('/api/auth/me').then((r) => j<{ user: BetaUser | null }>(r)).then((body) => {
      if (!body.user) {
        toSignIn();
        // Keep the cached account id available until toSignIn records a bound return intent.
        window.dispatchEvent(new CustomEvent('li-session-lost'));
      }
      return body;
    }),
};
